'use client';

import { useState, type CSSProperties } from 'react';
import Link from 'next/link';
import { IconArrowRight } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GPU_PRODUCTS, formatHourlyRate, getAvailableGPUs, type GPUProduct } from '@/lib/data/gpu-products';
import { GpuCharacter, HUES, personaFor } from '@/components/shop/homepage/gpu-characters';
import { Platform } from './arcade-art';

interface GPUPowerBattleProps {
  /** Hourly rate shown for a GPU (regional when a region filter is set). */
  priceOf: (gpu: GPUProduct) => number;
  /** Caption for the rates, e.g. "From" or the selected region. */
  priceLabel: string;
  /** Create-form link for a GPU. */
  hrefOf: (gpu: GPUProduct) => string | undefined;
}

const catalogIndex = (gpu: GPUProduct) => GPU_PRODUCTS.indexOf(gpu);
const shortName = (gpu: GPUProduct) => gpu.name.replace(/^NVIDIA\s+/, '');

/**
 * GPU vs GPU: two rentable GPUs face off on the facts the catalogue holds
 * (VRAM, vCPUs, RAM, hourly rate, listed use cases). No benchmarks.
 */
export function GPUPowerBattle({ priceOf, priceLabel, hrefOf }: GPUPowerBattleProps) {
  // Rentable products only: a withdrawn card has no rate to compare.
  const rentable = getAvailableGPUs();
  const [leftId, setLeftId] = useState(
    () => (rentable.find((g) => g.badge === 'Best Value') ?? rentable[0])?.id ?? ''
  );
  const [rightId, setRightId] = useState(
    () => (rentable.find((g) => g.badge === 'Popular' && g.id !== leftId) ?? rentable[1])?.id ?? ''
  );

  const left = rentable.find((g) => g.id === leftId);
  const right = rentable.find((g) => g.id === rightId);
  if (!left || !right) return null;

  const rows = [
    { label: 'VRAM', a: left.vram, b: right.vram, show: (v: number) => `${v} GB`, better: 'more' as const },
    { label: 'vCPUs', a: left.vcpus, b: right.vcpus, show: (v: number) => String(v), better: 'more' as const },
    { label: 'RAM', a: left.ram, b: right.ram, show: (v: number) => `${v} GB`, better: 'more' as const },
    {
      label: 'Hourly rate',
      a: priceOf(left),
      b: priceOf(right),
      show: (v: number) => formatHourlyRate(v),
      better: 'less' as const,
    },
  ];

  const shared = left.useCases.filter((u) => right.useCases.includes(u));
  const rateGap = Math.abs(priceOf(left) - priceOf(right));
  const pricier = priceOf(left) > priceOf(right) ? left : right;
  const cheaper = pricier === left ? right : left;

  return (
    <div className="rounded-[1.75rem] border border-border/60 bg-secondary/40 p-3 sm:p-5">
      {/* Fighters */}
      <div className="relative grid grid-cols-2 gap-3 sm:gap-5">
        <Fighter
          gpu={left}
          side="left"
          options={rentable}
          exclude={right.id}
          onPick={setLeftId}
          price={priceOf(left)}
          priceLabel={priceLabel}
          href={hrefOf(left)}
        />
        <Fighter
          gpu={right}
          side="right"
          options={rentable}
          exclude={left.id}
          onPick={setRightId}
          price={priceOf(right)}
          priceLabel={priceLabel}
          href={hrefOf(right)}
        />

        <div
          aria-hidden
          className="ga-burst pointer-events-none absolute left-1/2 top-[30%] z-20 grid size-16 -translate-x-1/2 -translate-y-1/2 place-items-center sm:size-24"
        >
          <span className="font-heading text-xl font-black italic text-foreground drop-shadow-[0_2px_0_var(--background)] sm:text-3xl">
            VS
          </span>
        </div>
      </div>

      {/* Stat bars */}
      <div className="mt-5 rounded-2xl border border-border/50 bg-background/50 p-4 sm:p-6">
        <dl className="space-y-4">
          {rows.map((row) => {
            const max = Math.max(row.a, row.b) || 1;
            const winner =
              row.a === row.b ? null : (row.better === 'more') === row.a > row.b ? 'left' : 'right';
            const tag = row.better === 'more' ? 'More' : 'Lower';
            return (
              <div key={row.label}>
                <dt className="mb-1.5 text-center font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  {row.label}
                  {winner === null && <span className="ml-2 text-foreground">· Same</span>}
                </dt>
                <dd className="grid grid-cols-[auto_1fr_1fr_auto] items-center gap-2 sm:gap-3">
                  <span className="flex min-w-[64px] items-center gap-1.5 font-heading text-sm font-bold tabular-nums text-foreground sm:min-w-[96px] sm:text-base">
                    {row.show(row.a)}
                    {winner === 'left' && <WinTag hue={HUES[personaFor(left.id, catalogIndex(left)).hue].cssVar}>{tag}</WinTag>}
                  </span>
                  <Bar
                    key={`${left.id}-${row.label}`}
                    fill={row.a / max}
                    hue={HUES[personaFor(left.id, catalogIndex(left)).hue].cssVar}
                    rtl
                  />
                  <Bar
                    key={`${right.id}-${row.label}`}
                    fill={row.b / max}
                    hue={HUES[personaFor(right.id, catalogIndex(right)).hue].cssVar}
                  />
                  <span className="flex min-w-[64px] items-center justify-end gap-1.5 font-heading text-sm font-bold tabular-nums text-foreground sm:min-w-[96px] sm:text-base">
                    {winner === 'right' && <WinTag hue={HUES[personaFor(right.id, catalogIndex(right)).hue].cssVar}>{tag}</WinTag>}
                    {row.show(row.b)}
                  </span>
                </dd>
              </div>
            );
          })}
        </dl>

        {/* Use cases */}
        <div className="mt-6 grid grid-cols-1 gap-3 border-t border-border/50 pt-5 sm:grid-cols-3">
          <UseCaseList title={`${shortName(left)} only`} items={left.useCases.filter((u) => !shared.includes(u))} />
          <UseCaseList title="Both" items={shared} highlight />
          <UseCaseList
            title={`${shortName(right)} only`}
            items={right.useCases.filter((u) => !shared.includes(u))}
            align="end"
          />
        </div>

        <p className="mt-5 text-center text-sm text-muted-foreground">
          {rateGap < 0.005 ? (
            <>Both run at the same hourly rate.</>
          ) : (
            <>
              The <span className="font-semibold text-foreground">{shortName(pricier)}</span> costs{' '}
              <span className="font-semibold tabular-nums text-foreground">{formatHourlyRate(rateGap)}</span> more
              than the <span className="font-semibold text-foreground">{shortName(cheaper)}</span>
              {pricier.vram > cheaper.vram && <> and has {pricier.vram - cheaper.vram} GB more VRAM</>}.
            </>
          )}{' '}
          Specs come from the catalogue; there are no benchmark claims here.
        </p>
      </div>
    </div>
  );
}

function Fighter({
  gpu,
  side,
  options,
  exclude,
  onPick,
  price,
  priceLabel,
  href,
}: {
  gpu: GPUProduct;
  side: 'left' | 'right';
  options: GPUProduct[];
  exclude: string;
  onPick: (id: string) => void;
  price: number;
  priceLabel: string;
  href?: string;
}) {
  const persona = personaFor(gpu.id, catalogIndex(gpu));
  const hue = HUES[persona.hue].cssVar;

  return (
    <div style={{ '--hue': hue } as CSSProperties} className="ga-vs-side relative flex flex-col rounded-2xl p-3 sm:p-5">
      <label className="sr-only" htmlFor={`battle-${side}`}>
        {side === 'left' ? 'First GPU' : 'Second GPU'}
      </label>
      <Select value={gpu.id} onValueChange={onPick}>
        <SelectTrigger
          id={`battle-${side}`}
          className="h-11 w-full rounded-xl border-[color-mix(in_oklab,var(--hue)_50%,transparent)] bg-background/60 font-heading font-semibold data-[size=default]:h-11"
        >
          <SelectValue placeholder="Select GPU" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id} disabled={option.id === exclude}>
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div aria-hidden className="relative mx-auto mt-4 w-[62%] max-w-[180px] sm:w-[52%]">
        <div key={gpu.id} className="ga-flip-in relative z-10">
          <div className={cn('bob-a', side === 'right' && '-scale-x-100')}>
            <GpuCharacter
              persona={persona}
              uid={`arcade-battle-${side}`}
              className="drop-shadow-[0_14px_18px_rgba(0,0,0,0.5)]"
            />
          </div>
        </div>
        <Platform hue={hue} className="-mt-[6%] h-6 w-full sm:h-8" />
      </div>

      <div className={cn('mt-3 flex flex-1 flex-col', side === 'right' ? 'items-end text-right' : 'items-start')}>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {persona.codename} · {persona.role}
        </p>
        <p className="font-heading text-xl font-bold leading-tight text-foreground sm:text-2xl">{shortName(gpu)}</p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{priceLabel}</p>
        <p className="font-heading text-lg font-bold tabular-nums text-foreground">{formatHourlyRate(price)}</p>
        <div className="min-h-3 flex-1" />
        {href && (
          <Button asChild className="ga-cta-hue mt-3 h-11 w-full rounded-xl font-bold hover:bg-transparent">
            <Link href={href}>
              <span className="truncate">Configure</span>
              <IconArrowRight className="size-4" />
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}

function Bar({ fill, hue, rtl = false }: { fill: number; hue: string; rtl?: boolean }) {
  return (
    <div aria-hidden className={cn('ga-bar-track flex h-3 overflow-hidden rounded-[4px] sm:h-4', rtl && 'justify-end')}>
      <span
        style={{ '--hue': hue, width: `${Math.max(6, Math.round(fill * 100))}%` } as CSSProperties}
        className={cn('ga-bar-fill meter-fill block h-full', rtl && 'ga-bar-rtl')}
      />
    </div>
  );
}

function WinTag({ hue, children }: { hue: string; children: string }) {
  return (
    <span
      style={{ '--hue': hue } as CSSProperties}
      className="ga-rarity hidden rounded px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider sm:inline"
    >
      {children}
    </span>
  );
}

function UseCaseList({
  title,
  items,
  highlight = false,
  align = 'start',
}: {
  title: string;
  items: string[];
  highlight?: boolean;
  align?: 'start' | 'end';
}) {
  return (
    <div className={cn(align === 'end' && 'sm:text-right')}>
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{title}</p>
      {items.length === 0 ? (
        <p className="mt-1.5 text-sm text-muted-foreground">—</p>
      ) : (
        <ul className={cn('mt-1.5 flex flex-wrap gap-1.5', align === 'end' && 'sm:justify-end')}>
          {items.map((item) => (
            <li
              key={item}
              className={cn(
                'rounded-md px-2 py-1 text-xs font-medium text-foreground',
                highlight ? 'bg-primary/15 ring-1 ring-primary/50' : 'bg-foreground/5 ring-1 ring-border'
              )}
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
